import type { QueryClient } from '@tanstack/react-query'
import { useAuth } from '@/store/auth.store'
import { useRecordingConsentStore } from '@/store/recording-consent.store'
import { toast } from 'sonner'

import type {
  MeetingRecorderReadinessType,
  MeetingRecordingConsentSurface,
} from '@scrambl/shared'
import {
  MEETING_RECORDING_CHAT_NOTICE,
  MEETING_RECORDING_CONSENT_VERSION,
} from '@scrambl/shared'

import { meetingRecorderApi } from './api'

export const meetingRecorderReadinessKey = (organizationId?: string) =>
  ['meeting-recorder-readiness', organizationId ?? null] as const

// A flat shape rather than a discriminated union: apps/web compiles without
// strictNullChecks, where `ok: true | false` does not narrow.
export interface RecordingPreflightResult {
  ok: boolean
  /** ok only: summaries can't be paid for (recording is still allowed). */
  isOutOfCredits?: boolean
  /** Not ok only. */
  reason?: 'unavailable' | 'plan' | 'consent_declined' | 'error'
  /** User-facing; null when the user simply cancelled. */
  message?: string | null
}

/**
 * This device's memory of the first-use notice: the same person, in the same
 * workspace, for the same consent text. The server's acknowledgement row is
 * the record; this only stops the dialog coming back if that row is ever
 * missing on a later read (it is quietly re-saved instead).
 */
function consentMemoryKey(organizationId?: string): string | null {
  const userId = useAuth.getState().user?.id
  if (!organizationId || !userId) return null
  return `scrambl:recording-consent:${MEETING_RECORDING_CONSENT_VERSION}:${organizationId}:${userId}`
}

export function rememberRecordingConsent(organizationId?: string): void {
  const key = consentMemoryKey(organizationId)
  if (!key) return
  try {
    localStorage.setItem(key, '1')
  } catch {
    /* Best effort: the server row is the record. */
  }
}

function hasRememberedRecordingConsent(organizationId?: string): boolean {
  const key = consentMemoryKey(organizationId)
  if (!key) return false
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

export const PLAN_LOCKED_MESSAGE =
  'Meeting recording is included with Pro and higher plans.'

/**
 * Run BEFORE any capture starts (desktop native, web mic, reminder, tray):
 * a fresh readiness read so a plan change, a disabled flag, or a missing
 * consent acknowledgement stops the start instead of producing audio the
 * API would refuse after stop. Asks for consent inline when required.
 */
export async function preflightRecordingStart(options: {
  queryClient: QueryClient
  organizationId?: string
  surface: MeetingRecordingConsentSurface
}): Promise<RecordingPreflightResult> {
  let readiness: MeetingRecorderReadinessType
  try {
    readiness = await options.queryClient.fetchQuery({
      queryKey: meetingRecorderReadinessKey(options.organizationId),
      queryFn: () => meetingRecorderApi.getReadiness(),
      staleTime: 0,
    })
  } catch {
    return {
      ok: false,
      reason: 'error',
      message: "Couldn't check recording access. Try again in a moment.",
    }
  }

  if (readiness.availability === 'locked') {
    return { ok: false, reason: 'plan', message: PLAN_LOCKED_MESSAGE }
  }
  if (readiness.availability !== 'available') {
    return {
      ok: false,
      reason: 'unavailable',
      message: "Meeting recording isn't available in this workspace.",
    }
  }

  if (!readiness.consent.isRequired) {
    rememberRecordingConsent(options.organizationId)
  } else {
    // Acknowledged once already on this device: re-save quietly. The dialog
    // is a one-time notice; later starts only get the reminder toast.
    let resaved = false
    if (hasRememberedRecordingConsent(options.organizationId)) {
      try {
        await meetingRecorderApi.acknowledgeConsent(options.surface)
        resaved = true
      } catch {
        // Fall back to asking again (e.g. the consent text changed).
      }
    }
    if (!resaved) {
      const acknowledged = await useRecordingConsentStore
        .getState()
        .request(options.surface)
      if (!acknowledged) {
        return { ok: false, reason: 'consent_declined', message: null }
      }
    }
  }

  return {
    ok: true,
    isOutOfCredits: readiness.credits?.canSummarize === false,
  }
}

/** Show a preflight failure (silent when the user cancelled consent). */
export function reportPreflightFailure(result: RecordingPreflightResult): void {
  if (result.ok || !result.message) return
  if (result.reason === 'plan') {
    toast.info('Recording is locked', { description: result.message })
    return
  }
  toast.error("Couldn't start recording", { description: result.message })
}

export async function copyRecordingNotice(): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(MEETING_RECORDING_CHAT_NOTICE)
    toast.success('Notice copied — paste it in the meeting chat')
    return true
  } catch {
    toast.error("Couldn't copy the notice", {
      description: MEETING_RECORDING_CHAT_NOTICE,
    })
    return false
  }
}

/**
 * Per-recording reminder at start (the full notice is a one-time dialog):
 * a warning to tell participants, with the one-click chat notice.
 */
export function announceRecordingStarted(options?: {
  title?: string
  isOutOfCredits?: boolean
}): void {
  toast.warning(options?.title ?? 'Recording started', {
    description: options?.isOutOfCredits
      ? 'Let everyone know they are being recorded. Your workspace is out of AI credits: the recording is saved, and you can generate notes once credits are added.'
      : 'Let everyone know they are being recorded.',
    action: {
      label: 'Copy notice to meeting chat',
      onClick: () => void copyRecordingNotice(),
    },
    duration: 15_000,
  })
}
